// System Console — support queue, performance, Sana volume, API cost.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, StatLine, Empty, useConsoleTheme, useIsMobile } from '../components/Console';
import { useSupportStats } from '../hooks/useAnalytics';
import { supabase } from '../supabaseClient';

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());
const USD_PER_M_TOK = 15; // ~$15 / 1M output tokens (estimate)
function usePerf() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_perf_summary').then(({ data }) => { if (!c) setD(data); }); return () => { c = true; }; }, []);
  return d;
}
function useHub(id) {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_get_hub_overview', { p_hub_id: id }).then(({ data }) => { if (!c) setD(data && data.metrics); }); return () => { c = true; }; }, [id]);
  return d;
}

function useCronHealth() {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    let x = false;
    const load = () => supabase.rpc('admin_cron_health').then(({ data }) => { if (!x) setD(data || []); });
    load();
    const iv = setInterval(load, 5 * 60000);
    return () => { x = true; clearInterval(iv); };
  }, []);
  return d;
}

function useRecentErrors() {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    let x = false;
    const load = () => supabase.rpc('admin_recent_errors', { p_limit: 50 }).then(({ data }) => { if (!x) setD(data || []); });
    load();
    const iv = setInterval(load, 120000);
    return () => { x = true; clearInterval(iv); };
  }, []);
  return d;
}

const CRON_WINDOW_H = { 'daily 01:00': 30, 'daily 02:00': 30, 'daily 03:00': 30, 'daily 03:30': 30, 'daily 04:00': 30, 'daily': 30, 'hourly': 4, 'every 30 min': 2 };

function Body() {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const { data: support } = useSupportStats();
  const perf = usePerf();
  const hub = useHub('system');
  const crons = useCronHealth();
  const errs = useRecentErrors();
  const lat = perf && perf.sana_latency_ms_24h;
  const series = (perf && perf.queries_by_day_30d) || [];
  const maxS = Math.max(1, ...series.map(x => x.count));
  const m = hub || [];
  const avgTokens = perf && perf.avg_tokens_out_24h;
  const queries24 = lat && lat.count;
  const estCost = (avgTokens && queries24) ? ((avgTokens * queries24) / 1e6 * USD_PER_M_TOK) : null;
  const col = n => (mobile ? '1fr 1fr' : `repeat(${n},1fr)`);
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(6), gap:10 }}>
        <KPI label="Open tickets" value={fmt(support.open)} sub={support.open > 0 ? 'queue' : 'all clear'} subColor={support.open > 0 ? t.warn : t.up} accent={support.open > 0 ? '#e53935' : '#17a86a'} />
        <KPI label="In progress" value={fmt(support.inProgress)} accent="#d97706" />
        <KPI label="Resolved" value={fmt(support.resolved)} subColor={t.up} accent="#17a86a" />
        <KPI label="Sana · 24h" value={lat && lat.count != null ? fmt(lat.count) : '…'} sub="queries" accent="#5a8fa6" />
        <KPI label="p95 latency" value={lat && lat.p95 != null ? `${fmt(lat.p95)}ms` : '—'} sub="24h" accent="#9F7E4F" />
        <KPI label="API cost · 24h" value={estCost != null ? `$${estCost.toFixed(2)}` : '—'} sub="Sana tokens (est.)" accent="#314F5C" />
      </div>

      <SectionLabel>Support queue</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◷ Ticket health">
          <StatLine label="Open" value={fmt(support.open)} color={support.open > 0 ? t.warn : t.tx} />
          <StatLine label="In progress" value={fmt(support.inProgress)} />
          <StatLine label="Resolved" value={fmt(support.resolved)} color={t.up} />
          <StatLine label="Unassigned" value={fmt(support.unassigned)} color={support.unassigned > 0 ? t.danger : t.tx} />
          <StatLine label="Total" value={fmt(support.total)} />
        </Panel>
        <Panel title="◈ Hub vitals">
          {m.length === 0 ? <Empty>Loading…</Empty> : m.map((k, i) => <StatLine key={i} label={k.label} value={k.value != null ? fmt(k.value) : '…'} />)}
        </Panel>
      </div>

      <SectionLabel right="last output per cron · evidence-based">Automation health</SectionLabel>
      <Panel style={{ marginBottom: 4 }}>
        {!crons ? <Empty>Loading cron evidence…</Empty> : (
          <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: '2px 24px' }}>
            {crons.map((c, i) => {
              const ageH = c.last_evidence ? (Date.now() - new Date(c.last_evidence)) / 36e5 : null;
              const win = CRON_WINDOW_H[c.cadence] || 30;
              const state = ageH == null ? 'quiet' : ageH <= win ? 'ok' : ageH <= win * 3 ? 'late' : 'stale';
              const color = state === 'ok' ? t.up : state === 'late' ? t.warn : state === 'stale' ? t.danger : t.mut;
              const ageTxt = ageH == null ? 'no output yet' : ageH < 1 ? Math.round(ageH * 60) + 'm ago' : ageH < 48 ? Math.round(ageH) + 'h ago' : Math.round(ageH / 24) + 'd ago';
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:9, padding:'6px 0', borderBottom:`1px solid ${t.bd}`, fontSize:'0.88rem' }} title={'Evidence table: ' + c.evidence}>
                  <span style={{ width:8, height:8, borderRadius:'50%', background:color, flexShrink:0 }} />
                  <span style={{ flex:1, color:t.tx, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.cron}</span>
                  <span style={{ color:t.mut, fontSize:'0.78rem', whiteSpace:'nowrap' }}>{c.cadence}</span>
                  <span style={{ color, fontWeight:600, fontSize:'0.82rem', whiteSpace:'nowrap', minWidth:86, textAlign:'right' }}>{ageTxt}</span>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ fontSize:'0.74rem', color:t.mut, marginTop:10 }}>
          Health is inferred from each cron's most recent database output. A quiet cron with nothing to do shows "no output yet" — grey, not red.
        </div>
      </Panel>

      <SectionLabel right={errs ? `${errs.length} recent` : undefined}>Runtime errors</SectionLabel>
      <Panel style={{ marginBottom: 4 }}>
        {!errs ? <Empty>Loading error log…</Empty> : errs.length === 0 ? (
          <Empty>No runtime errors recorded in sana_error_log. (Sentry env var is still unset — crash reporting beyond Sana needs SENTRY_DSN on Vercel.)</Empty>
        ) : (
          <div>
            {errs.slice(0, 10).map((e, i) => (
              <div key={i} style={{ display:'flex', gap:10, padding:'7px 0', borderBottom:`1px solid ${t.bd}`, fontSize:'0.84rem', alignItems:'baseline' }}>
                <span style={{ color:t.mut, whiteSpace:'nowrap', fontFamily:'monospace', fontSize:'0.72rem' }}>{(e.at || '').slice(0, 16).replace('T', ' ')}</span>
                <span style={{ color:t.danger, fontWeight:600, whiteSpace:'nowrap', fontSize:'0.76rem' }}>{e.error_type || 'error'}{e.status_code ? ' · ' + e.status_code : ''}</span>
                <span style={{ color:t.tx, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }} title={e.detail}>{e.detail}{e.query_text ? ' — “' + e.query_text + '”' : ''}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <SectionLabel>Performance</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◔ Sana latency" right="24h">
          <StatLine label="p50" value={lat && lat.p50 != null ? `${fmt(lat.p50)}ms` : '—'} />
          <StatLine label="p95" value={lat && lat.p95 != null ? `${fmt(lat.p95)}ms` : '—'} color={t.warn} />
          <StatLine label="p99" value={lat && lat.p99 != null ? `${fmt(lat.p99)}ms` : '—'} color={t.danger} />
          <StatLine label="Avg tokens out" value={avgTokens != null ? fmt(avgTokens) : '—'} />
        </Panel>
        <Panel title="◠ Sana volume · 30d">
          {series.length === 0 ? <Empty>No Sana query log in the last 30 days.</Empty>
            : (
              <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:110 }}>
                {series.map((x, i) => <div key={i} style={{ flex:1, background:t.up, height:`${Math.round(x.count / maxS * 100)}%`, minHeight: x.count ? 2 : 0, borderRadius:'2px 2px 0 0' }} title={`${x.day}: ${x.count}`} />)}
              </div>
            )}
        </Panel>
      </div>
    </div>
  );
}

export default function SystemConsole() {
  return <ConsoleShell title="System Console"><Body /></ConsoleShell>;
}
